import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./ImageSearch.module.scss";
import { TbPhotoPlus } from "react-icons/tb";
import { IoClose } from "react-icons/io5";
import HeaderProductCategories from "../../layouts/Header/headerServices/HeaderServices";
import { fileToDataUrl } from "../../helper/imageConverter";

export interface ImageSearchProps {
  file?: File | null;
  channel?: string;
  page?: number;
  perPage?: number;
  isOpen?: boolean;
  onClose?: () => void;
  onSearchResults?: (
    products: any[],
    file: File,
    previewUrl?: string,
    meta?: {
      pagination?: any;
      picRegionInfo?: any;
      requestId?: string;
    }
  ) => void;
  onSearchStart?: (file?: File, previewUrl?: string) => void;
  onSearchError?: (error: any) => void;
  onFileSelect?: (file: File) => void;
  className?: string;
}

const ImageSearch: React.FC<ImageSearchProps> = ({
  file: initialFile,
  channel = "1688",
  page = 1,
  perPage = 20,
  isOpen = true,
  onClose,
  onSearchResults,
  onSearchStart,
  onSearchError,
  onFileSelect,
  className = "",
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState<string | null>(null);
  const [isSearchingImage, setIsSearchingImage] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const PhotoIcon = TbPhotoPlus as any;
  const CloseSearchIcon = IoClose as any;

  const handleProcessFile = useCallback(
    async (fileToProcess: File) => {
      if (!fileToProcess) return;
      const isImageFile =
        Boolean(fileToProcess.type && fileToProcess.type.startsWith("image/")) ||
        /\.(jpe?g|png|webp|gif|bmp|heic|heif|svg)$/i.test(fileToProcess.name);
      if (!isImageFile) return;

      setIsSearchingImage(true);

      try {
        const base64DataUrl = await fileToDataUrl(fileToProcess);
        setSelectedImagePreview(base64DataUrl);
        setSelectedFile(fileToProcess);

        if (onFileSelect) {
          onFileSelect(fileToProcess);
        }

        if (onSearchStart) {
          onSearchStart(fileToProcess, base64DataUrl);
        }

        const response = await HeaderProductCategories.searchProductsByImage(
          base64DataUrl,
          channel,
          page,
          perPage
        );

        const responseData = response?.data;
        const products: any[] = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData?.products)
          ? responseData.products
          : Array.isArray(responseData)
          ? responseData
          : [];

        if (onSearchResults) {
          onSearchResults(products, fileToProcess, base64DataUrl, {
            pagination: responseData?.pagination,
            picRegionInfo: responseData?.pic_region_info,
            requestId: responseData?.request_id,
          });
        }
      } catch (error) {
        console.error("Şəkillə axtarış xətası:", error);
        if (onSearchError) {
          onSearchError(error);
        }
      } finally {
        setIsSearchingImage(false);
      }
    },
    [channel, page, perPage, onFileSelect, onSearchStart, onSearchResults, onSearchError]
  );

  // When `file` prop is provided or changed from outside
  useEffect(() => {
    if (!initialFile) return;
    handleProcessFile(initialFile);
  }, [initialFile, handleProcessFile]);

  // Clipboard Paste listener (Ctrl+V) & Click Outside logic
  useEffect(() => {
    if (!isOpen) return;

    const handlePaste = (event: any) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type && item.type.indexOf("image") !== -1) {
          const pastedFile = item.getAsFile();
          if (pastedFile) {
            handleProcessFile(pastedFile);
            break;
          }
        }
      }
    };

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (!target) return;
      if (popoverRef.current && popoverRef.current.contains(target as Node)) {
        return;
      }
      if (
        target.closest &&
        (target.closest('[aria-label="Şəkillə axtarış"]') ||
          target.closest(`.${styles.image_search_popover}`))
      ) {
        return;
      }
      handleClose();
    };

    window.addEventListener("paste", handlePaste as any);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      window.removeEventListener("paste", handlePaste as any);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, handleProcessFile]);

  const handleUploadBtnClick = () => {
    if (!imageInputRef.current) return;
    imageInputRef.current.click();
  };

  const handleImageFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleProcessFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    handleProcessFile(file);
  };

  const handleClose = () => {
    setSelectedImagePreview((prevUrl) => {
      if (prevUrl) {
        URL.revokeObjectURL(prevUrl);
      }
      return null;
    });
    setSelectedFile(null);
    setIsSearchingImage(false);
    setIsDragging(false);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
    if (onClose) {
      onClose();
    }
  };

  const handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleClose();
    }
  };

  const handleDropzoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleUploadBtnClick();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className={`${styles.image_search_popover} ${className}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handlePopoverKeyDown}
      tabIndex={0}
      aria-label="Şəkillə axtarış paneli"
    >
      <input
        type="file"
        id="image-search-file-input"
        accept="image/*,.heic,.heif"
        ref={imageInputRef}
        onChange={handleImageFileSelect}
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          border: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
        tabIndex={-1}
        aria-hidden="true"
      />

      <div className={styles.popover_header}>
        <span className={styles.popover_title}>Şəkillə axtarış</span>
        <button
          type="button"
          className={styles.popover_close_btn}
          onClick={handleClose}
          aria-label="Bağla"
          tabIndex={0}
        >
          <CloseSearchIcon size={18} />
        </button>
      </div>

      <div
        className={`${styles.popover_dropzone} ${isDragging ? styles.dropzone_active : ""} ${
          selectedImagePreview ? styles.dropzone_has_preview : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleUploadBtnClick}
        onKeyDown={handleDropzoneKeyDown}
        tabIndex={0}
        aria-label="Şəkli bura sürükləyin və ya seçin"
      >
        {selectedImagePreview ? (
          <div className={styles.preview_container}>
            <img
              src={selectedImagePreview}
              alt={selectedFile?.name || "Seçilmiş şəkil"}
              className={styles.preview_img}
            />
            {isSearchingImage && (
              <div className={styles.scanning_overlay}>
                <div className={styles.scan_line} />
                <span>Axtarılır...</span>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.dropzone_placeholder}>
            <div className={styles.dropzone_icon_wrapper}>
              <PhotoIcon size={38} className={styles.dropzone_icon} />
            </div>
            <p className={styles.dropzone_text}>
              Şəkli bura yükləyin
            </p>
          </div>
        )}
      </div>

      <div className={styles.popover_footer}>
        <button
          type="button"
          className={styles.upload_action_btn}
          onClick={handleUploadBtnClick}
          tabIndex={0}
          aria-label={selectedImagePreview ? "Başqa şəkil seçin" : "Şəkil yüklə"}
        >
          {selectedImagePreview ? "Başqa şəkil seçin" : "Şəkil yüklə"}
        </button>
      </div>
    </div>
  );
};

export default ImageSearch;
